import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Searchable dropdown built on shadcn Command + Popover.
 *
 * Props:
 *   options       [{ value, label, sublabel? }]
 *   value         string
 *   onChange(val)
 *   placeholder   default "Select…"
 *   emptyText     default "No matches"
 *   className     forwarded to trigger
 *   testId        data-testid prefix for trigger + list items
 *   disabled
 */
export default function SearchSelect({ options = [], value, onChange, placeholder = "Select…",
                                       emptyText = "No matches", className, testId, disabled, wrap = false, contentClassName, large = false }) {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((o) => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} disabled={disabled}
                data-testid={testId ? `${testId}-trigger` : undefined}
                className={cn("rounded-sm h-9 w-full justify-between font-normal", className,
                  !selected && "text-muted-foreground")}>
          <span className="truncate text-left">{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("p-0", contentClassName || "w-[var(--radix-popover-trigger-width)]")} align="start">
        <Command shouldFilter>
          <CommandInput placeholder="Search…" className={large ? "h-11 text-base" : "h-9"} data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList className={large ? "max-h-[60vh]" : undefined}>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem key={o.value} value={`${o.label} ${o.sublabel || ""} ${o.value}`}
                             onSelect={() => { onChange(o.value); setOpen(false); }}
                             data-testid={testId ? `${testId}-item-${o.value}` : undefined}>
                  <Check className={cn("mr-2 h-4 w-4", value === o.value ? "opacity-100" : "opacity-0")} />
                  <div className="min-w-0 flex-1">
                    <div className={cn(large ? "text-[15px] font-medium py-0.5" : "text-sm", wrap ? "whitespace-normal leading-snug" : "truncate")}>{o.label}</div>
                    {o.sublabel && <div className={cn(large ? "text-xs opacity-75" : "text-[10px] text-muted-foreground", !wrap && "truncate")}>{o.sublabel}</div>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
